import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import GroupsIcon from '@mui/icons-material/Groups';

const DepartmentSupervision = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [reportData, setReportData] = useState({
    date: '',
    departmentGoals: '',
    challengesFaced: '',
    proposedSolutions: '',
    resourceNeeds: ''
  });

  // Sample departments data
  const departments = [
    {
      id: 1,
      name: 'Mathematics Department',
      head: 'Dr. John Smith',
      teachers: 5,
      completedTasks: 12,
      pendingTasks: 3
    },
    {
      id: 2,
      name: 'English Department',
      head: 'Prof. Sarah Johnson',
      teachers: 6,
      completedTasks: 15,
      pendingTasks: 2
    },
    {
      id: 3,
      name: 'Science Department',
      head: 'Dr. Michael Brown',
      teachers: 7,
      completedTasks: 10,
      pendingTasks: 4
    }
  ];

  // Sample department reports
  const departmentReports = [
    {
      id: 1,
      department: 'Mathematics',
      date: '2024-01-15',
      status: 'Completed',
      submittedBy: 'Dr. John Smith'
    },
    {
      id: 2,
      department: 'English',
      date: '2024-01-18',
      status: 'Pending Review',
      submittedBy: 'Prof. Sarah Johnson'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({
      selectedDepartment,
      reportData
    });
    // Here you would typically save to your backend
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Department Supervision Dashboard
      </Typography>

      {/* Department Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {departments.map((dept) => (
          <Grid item xs={12} md={4} key={dept.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {dept.name}
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Head: {dept.head}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <GroupsIcon color="primary" />
                      <Typography variant="body2">
                        {dept.teachers} Teachers
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <AssignmentTurnedInIcon color="success" />
                      <Typography variant="body2">
                        {dept.completedTasks} Done
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <PendingActionsIcon color="warning" />
                      <Typography variant="body2">
                        {dept.pendingTasks} Pending
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Department Report Form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Submit Department Report
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Department</InputLabel>
                <Select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  label="Select Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Report Date"
                value={reportData.date}
                onChange={(e) => setReportData({...reportData, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Department Goals and Objectives"
                value={reportData.departmentGoals}
                onChange={(e) => setReportData({...reportData, departmentGoals: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Challenges Faced"
                value={reportData.challengesFaced}
                onChange={(e) => setReportData({...reportData, challengesFaced: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Proposed Solutions"
                value={reportData.proposedSolutions}
                onChange={(e) => setReportData({...reportData, proposedSolutions: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Resource Needs"
                value={reportData.resourceNeeds}
                onChange={(e) => setReportData({...reportData, resourceNeeds: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
              >
                Submit Report
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Previous Reports Table */}
      <Typography variant="h6" gutterBottom>
        Department Reports History
      </Typography>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Submitted By</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departmentReports.map((report) => (
              <TableRow key={report.id}>
                <TableCell>{report.date}</TableCell>
                <TableCell>{report.department}</TableCell>
                <TableCell>{report.submittedBy}</TableCell>
                <TableCell>
                  <Chip
                    label={report.status}
                    color={report.status === 'Completed' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default DepartmentSupervision;
